import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  Edit,
  Trash2,
  Save,
  Settings,
  Palette,
  Users,
  X,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

const AdminLogen = () => {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('loges');
  const [loading, setLoading] = useState(true);

  // Loges state
  const [loges, setLoges] = useState([]);
  const [showLogeDialog, setShowLogeDialog] = useState(false);
  const [editingLoge, setEditingLoge] = useState(null);
  const [logeForm, setLogeForm] = useState({ nummer: '', kapazitaet: 12, sort_order: 0, is_active: true });

  // Dauerlogen state
  const [permanent, setPermanent] = useState([]);
  const [showPermanentDialog, setShowPermanentDialog] = useState(false);
  const [permanentForm, setPermanentForm] = useState({
    loge_id: '', kunde: '', kundennummer: '', standard_anzahl: 0,
    catering: '', art: '', hospitality_partner: '', ansprechpartner: '', infos: ''
  });

  // Config state
  const [config, setConfig] = useState({ catering: [], art: [], hospitality: [], status_colors: {} });
  const [configEditing, setConfigEditing] = useState(null);
  const [configNewValue, setConfigNewValue] = useState('');
  const [colorForm, setColorForm] = useState({});

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [logesRes, permRes, configRes] = await Promise.all([
        axios.get('/loges'),
        axios.get('/loges/permanent'),
        axios.get('/loges/config')
      ]);
      setLoges(logesRes.data);
      setPermanent(permRes.data);
      setConfig({
        catering: configRes.data.catering?.values || [],
        art: configRes.data.art?.values || [],
        hospitality: configRes.data.hospitality?.values || [],
        status_colors: configRes.data.status_colors?.colors || { leer: '#e5e7eb', reserviert: '#fef3c7', gebucht: '#d1fae5' }
      });
      setColorForm(configRes.data.status_colors?.colors || { leer: '#e5e7eb', reserviert: '#fef3c7', gebucht: '#d1fae5' });
    } catch (err) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  // === LOGE CRUD ===
  const handleSaveLoge = async () => {
    if (!logeForm.nummer.trim()) { toast.error('Logen-Nummer erforderlich'); return; }
    try {
      if (editingLoge) {
        await axios.patch(`/loges/${editingLoge.id}`, {
          nummer: logeForm.nummer,
          kapazitaet: parseInt(logeForm.kapazitaet),
          sort_order: parseInt(logeForm.sort_order),
          is_active: logeForm.is_active
        });
        toast.success('Loge aktualisiert');
      } else {
        await axios.post('/loges', {
          nummer: logeForm.nummer,
          kapazitaet: parseInt(logeForm.kapazitaet),
          sort_order: parseInt(logeForm.sort_order),
          is_active: logeForm.is_active
        });
        toast.success('Loge erstellt');
      }
      setShowLogeDialog(false);
      setEditingLoge(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDeleteLoge = async (loge) => {
    if (!window.confirm(`Loge ${loge.nummer} wirklich löschen?`)) return;
    try {
      await axios.delete(`/loges/${loge.id}`);
      toast.success('Loge gelöscht');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const openEditLoge = (loge) => {
    setEditingLoge(loge);
    setLogeForm({ nummer: loge.nummer, kapazitaet: loge.kapazitaet, sort_order: loge.sort_order, is_active: loge.is_active });
    setShowLogeDialog(true);
  };

  const openNewLoge = () => {
    setEditingLoge(null);
    setLogeForm({ nummer: '', kapazitaet: 12, sort_order: loges.length, is_active: true });
    setShowLogeDialog(true);
  };

  // === DAUERLOGEN ===
  const handleSavePermanent = async () => {
    if (!permanentForm.loge_id || !permanentForm.kunde.trim()) {
      toast.error('Loge und Kundenname erforderlich');
      return;
    }
    try {
      await axios.post('/loges/permanent', permanentForm);
      toast.success('Dauerloge erstellt');
      setShowPermanentDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const handleDeletePermanent = async (id) => {
    if (!window.confirm('Dauerloge wirklich löschen?')) return;
    try {
      await axios.delete(`/loges/permanent/${id}`);
      toast.success('Dauerloge gelöscht');
      fetchAll();
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  const openNewPermanent = () => {
    setPermanentForm({
      loge_id: loges[0]?.id || '', kunde: '', kundennummer: '', standard_anzahl: 0,
      catering: '', art: '', hospitality_partner: '', ansprechpartner: '', infos: ''
    });
    setShowPermanentDialog(true);
  };

  // === CONFIG ===
  const handleAddConfigValue = async (type) => {
    if (!configNewValue.trim()) return;
    const updated = [...config[type], configNewValue.trim()];
    try {
      await axios.patch(`/loges/config/${type}`, { values: updated });
      setConfig(prev => ({ ...prev, [type]: updated }));
      setConfigNewValue('');
      toast.success('Hinzugefügt');
    } catch (err) {
      toast.error('Fehler');
    }
  };

  const handleRemoveConfigValue = async (type, value) => {
    const updated = config[type].filter(v => v !== value);
    try {
      await axios.patch(`/loges/config/${type}`, { values: updated });
      setConfig(prev => ({ ...prev, [type]: updated }));
      toast.success('Entfernt');
    } catch (err) {
      toast.error('Fehler');
    }
  };

  const handleSaveColors = async () => {
    try {
      await axios.patch('/loges/config/status_colors', { colors: colorForm });
      setConfig(prev => ({ ...prev, status_colors: colorForm }));
      toast.success('Farben gespeichert');
    } catch (err) {
      toast.error('Fehler beim Speichern');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><span className="text-gray-500">Lade...</span></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-logen-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Logen-Verwaltung</h1>
        <p className="text-sm text-gray-500 mt-1">Logen, Dauerlogen und Konfiguration verwalten</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="admin-logen-tabs">
          <TabsTrigger value="loges" data-testid="tab-loges">
            <Building2 className="w-4 h-4 mr-1.5" />Logen
          </TabsTrigger>
          <TabsTrigger value="permanent" data-testid="tab-permanent">
            <Users className="w-4 h-4 mr-1.5" />Dauerlogen
          </TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config">
            <Settings className="w-4 h-4 mr-1.5" />Konfiguration
          </TabsTrigger>
        </TabsList>

        {/* === LOGEN TAB === */}
        <TabsContent value="loges">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Logen ({loges.length})</CardTitle>
                <CardDescription>Globale Logen-Definitionen für alle Spiele</CardDescription>
              </div>
              <Button onClick={openNewLoge} data-testid="add-loge-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Neue Loge
              </Button>
            </CardHeader>
            <CardContent>
              {loges.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Noch keine Logen angelegt</p>
              ) : (
                <div className="space-y-2">
                  {loges.map(loge => (
                    <div key={loge.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800" data-testid={`admin-loge-${loge.nummer}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-[#e30613]/10 flex items-center justify-center">
                          <span className="font-bold text-[#e30613]">{loge.nummer}</span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">Loge {loge.nummer}</p>
                          <p className="text-sm text-gray-500">Kapazität: {loge.kapazitaet} Plätze &middot; Sortierung: {loge.sort_order}</p>
                        </div>
                        {!loge.is_active && (
                          <Badge variant="secondary" className="text-xs">Inaktiv</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEditLoge(loge)} data-testid={`edit-loge-${loge.nummer}-btn`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDeleteLoge(loge)} data-testid={`delete-loge-${loge.nummer}-btn`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === DAUERLOGEN TAB === */}
        <TabsContent value="permanent">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Dauerlogen ({permanent.length})</CardTitle>
                <CardDescription>Saisonbuchungen, die automatisch auf Spieltage angewendet werden</CardDescription>
              </div>
              <Button onClick={openNewPermanent} data-testid="add-permanent-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Neue Dauerloge
              </Button>
            </CardHeader>
            <CardContent>
              {permanent.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Noch keine Dauerlogen angelegt</p>
              ) : (
                <div className="space-y-2">
                  {permanent.map(p => (
                    <div key={p.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800" data-testid={`permanent-${p.id}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                          <span className="font-bold text-purple-700 dark:text-purple-300 text-sm">{p.loge?.nummer || '?'}</span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{p.kunde}</p>
                          <p className="text-sm text-gray-500">
                            Loge {p.loge?.nummer || '?'} &middot; {p.standard_anzahl} Personen
                            {p.kundennummer && ` \u2022 ${p.kundennummer}`}
                          </p>
                          {(p.catering || p.art) && (
                            <p className="text-xs text-gray-400">
                              {p.catering && `Catering: ${p.catering}`}
                              {p.catering && p.art && ' \u2022 '}
                              {p.art && `Art: ${p.art}`}
                            </p>
                          )}
                        </div>
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => handleDeletePermanent(p.id)} data-testid={`delete-permanent-${p.id}-btn`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === CONFIG TAB === */}
        <TabsContent value="config">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Dropdown Configs */}
            {[
              { key: 'catering', label: 'Catering-Optionen' },
              { key: 'art', label: 'Art-Optionen' },
              { key: 'hospitality', label: 'Hospitality/Partner' }
            ].map(({ key, label }) => (
              <Card key={key} data-testid={`config-${key}`}>
                <CardHeader>
                  <CardTitle className="text-base">{label}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {config[key].map((val, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                        <span className="text-sm">{val}</span>
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleRemoveConfigValue(key, val)}>
                          <X className="w-3 h-3 text-red-500" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex gap-2 mt-3">
                      <Input
                        placeholder={`Neuer Wert...`}
                        value={configEditing === key ? configNewValue : ''}
                        onFocus={() => { setConfigEditing(key); setConfigNewValue(''); }}
                        onChange={(e) => { setConfigEditing(key); setConfigNewValue(e.target.value); }}
                        onKeyDown={(e) => e.key === 'Enter' && configEditing === key && handleAddConfigValue(key)}
                        className="h-8 text-sm"
                        data-testid={`config-${key}-input`}
                      />
                      <Button size="sm" className="h-8" onClick={() => handleAddConfigValue(key)} disabled={configEditing !== key || !configNewValue.trim()} data-testid={`config-${key}-add-btn`}>
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Status Colors */}
            <Card data-testid="config-colors">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Palette className="w-4 h-4" /> Status-Farben
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { key: 'leer', label: 'Leer' },
                    { key: 'reserviert', label: 'Reserviert' },
                    { key: 'gebucht', label: 'Gebucht' }
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded border" style={{ backgroundColor: colorForm[key] || '#ccc' }} />
                      <Label className="w-20 text-sm">{label}</Label>
                      <Input
                        type="color"
                        value={colorForm[key] || '#cccccc'}
                        onChange={(e) => setColorForm(prev => ({ ...prev, [key]: e.target.value }))}
                        className="w-12 h-8 p-0.5 cursor-pointer"
                        data-testid={`color-${key}-input`}
                      />
                      <Input
                        value={colorForm[key] || ''}
                        onChange={(e) => setColorForm(prev => ({ ...prev, [key]: e.target.value }))}
                        className="h-8 text-xs font-mono w-24"
                      />
                    </div>
                  ))}
                  <Button size="sm" onClick={handleSaveColors} className="mt-2" data-testid="save-colors-btn">
                    <Save className="w-4 h-4 mr-1.5" /> Farben speichern
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* === LOGE DIALOG === */}
      <Dialog open={showLogeDialog} onOpenChange={setShowLogeDialog}>
        <DialogContent data-testid="loge-dialog">
          <DialogHeader>
            <DialogTitle>{editingLoge ? 'Loge bearbeiten' : 'Neue Loge'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Logen-Nummer</Label>
              <Input
                value={logeForm.nummer}
                onChange={e => setLogeForm(p => ({ ...p, nummer: e.target.value }))}
                placeholder="z.B. 401"
                data-testid="loge-nummer-input"
              />
            </div>
            <div>
              <Label>Kapazität (Plätze)</Label>
              <Input
                type="number"
                value={logeForm.kapazitaet}
                onChange={e => setLogeForm(p => ({ ...p, kapazitaet: parseInt(e.target.value) || 0 }))}
                data-testid="loge-kapazitaet-input"
              />
            </div>
            <div>
              <Label>Sortierung</Label>
              <Input
                type="number"
                value={logeForm.sort_order}
                onChange={e => setLogeForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={logeForm.is_active}
                onCheckedChange={(checked) => setLogeForm(p => ({ ...p, is_active: checked }))}
                data-testid="loge-active-switch"
              />
              <Label>Aktiv für die Saison</Label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowLogeDialog(false)}>Abbrechen</Button>
              <Button onClick={handleSaveLoge} data-testid="save-loge-btn">
                <Save className="w-4 h-4 mr-1.5" /> Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* === DAUERLOGE DIALOG === */}
      <Dialog open={showPermanentDialog} onOpenChange={setShowPermanentDialog}>
        <DialogContent className="max-w-lg" data-testid="permanent-dialog">
          <DialogHeader>
            <DialogTitle>Neue Dauerloge</DialogTitle>
            <DialogDescription>Erstellt eine Saisonbuchung, die auf jeden Spieltag angewendet werden kann</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div>
              <Label>Loge</Label>
              <Select value={permanentForm.loge_id} onValueChange={v => setPermanentForm(p => ({ ...p, loge_id: v }))}>
                <SelectTrigger data-testid="permanent-loge-select">
                  <SelectValue placeholder="Loge auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {loges.filter(l => l.is_active).map(l => (
                    <SelectItem key={l.id} value={l.id}>Loge {l.nummer} ({l.kapazitaet} Pl.)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kunde</Label>
                <Input value={permanentForm.kunde} onChange={e => setPermanentForm(p => ({ ...p, kunde: e.target.value }))} data-testid="permanent-kunde-input" />
              </div>
              <div>
                <Label>Kundennummer</Label>
                <Input value={permanentForm.kundennummer} onChange={e => setPermanentForm(p => ({ ...p, kundennummer: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Standard-Personenzahl</Label>
              <Input type="number" value={permanentForm.standard_anzahl} onChange={e => setPermanentForm(p => ({ ...p, standard_anzahl: parseInt(e.target.value) || 0 }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Catering</Label>
                <Select value={permanentForm.catering || '_none'} onValueChange={v => setPermanentForm(p => ({ ...p, catering: v === '_none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">-</SelectItem>
                    {config.catering.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Art</Label>
                <Select value={permanentForm.art || '_none'} onValueChange={v => setPermanentForm(p => ({ ...p, art: v === '_none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">-</SelectItem>
                    {config.art.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Hospitality/Partner</Label>
                <Select value={permanentForm.hospitality_partner || '_none'} onValueChange={v => setPermanentForm(p => ({ ...p, hospitality_partner: v === '_none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">-</SelectItem>
                    {config.hospitality.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ansprechpartner</Label>
                <Input value={permanentForm.ansprechpartner} onChange={e => setPermanentForm(p => ({ ...p, ansprechpartner: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Infos / Notizen</Label>
              <Input value={permanentForm.infos} onChange={e => setPermanentForm(p => ({ ...p, infos: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowPermanentDialog(false)}>Abbrechen</Button>
              <Button onClick={handleSavePermanent} data-testid="save-permanent-btn">
                <Save className="w-4 h-4 mr-1.5" /> Erstellen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLogen;
